import * as React from "react";
import { useGLTF } from "@react-three/drei";
import type { GroupProps } from "@react-three/fiber";
import { useEffect, useMemo } from "react";
import * as THREE from "three";
import boxGlbUrl from "../assets/heavens_bridge_box.glb";

type HeavensBridgeBoxProps = GroupProps & {
  /** Auto-fit the model so its largest dimension ~= `fit` world units. */
  fit?: number;
  /** IBL contribution multiplier for all standard materials inside the model. */
  envMapIntensity?: number;
};

const BOX_PATH = boxGlbUrl;

export default function HeavensBridgeBox({
  fit = 5,
  envMapIntensity = 0,
  ...props
}: HeavensBridgeBoxProps) {
  const { scene } = useGLTF(BOX_PATH);

  const sceneClone = useMemo(() => {
    const clone = scene.clone(true);
    clone.traverse((child) => {
      if ((child as THREE.Mesh).isMesh) {
        const mesh = child as THREE.Mesh;
        const materials = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        for (const mat of materials) {
          if (mat instanceof THREE.MeshStandardMaterial) {
            mat.envMapIntensity = envMapIntensity;
            if (mat.map) {
              mat.map.anisotropy = 16;
              mat.map.colorSpace = THREE.SRGBColorSpace;
              mat.map.minFilter = THREE.LinearMipmapLinearFilter;
              mat.map.magFilter = THREE.LinearFilter;
              mat.map.needsUpdate = true;
            }
          }
        }
      }
    });
    return clone;
    // envMapIntensity intentionally excluded — tracked reactively in the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene]);

  // Reactive update so Leva tuning of envMapIntensity takes effect without rebuild.
  useEffect(() => {
    sceneClone.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (!mesh.isMesh) return;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        if (mat instanceof THREE.MeshStandardMaterial) {
          mat.envMapIntensity = envMapIntensity;
        }
      }
    });
  }, [sceneClone, envMapIntensity]);

  const { fitScale, pivotOffset } = useMemo(() => {
    const box = new THREE.Box3().setFromObject(sceneClone);
    const size = box.getSize(new THREE.Vector3());
    const height = size.y;

    const center = box.getCenter(new THREE.Vector3());
    const scale = Number.isFinite(height) && height > 0 ? fit / height : 1;

    const offset: [number, number, number] = [-center.x, -center.y, -center.z];

    return { fitScale: scale, pivotOffset: offset };
  }, [sceneClone, fit]);

  return (
    <group {...props}>
      <group scale={fitScale}>
        <primitive object={sceneClone} position={pivotOffset} />
      </group>
    </group>
  );
}

useGLTF.preload(BOX_PATH);
